import { useMutation } from "@apollo/client/react";
import {
  ContentStatus,
  KurssloeyfeEditData,
  KurssloeyfeEditDataVariables,
  UpdateKurssloeyfe,
  UpdateKurssloeyfeVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import { contentStatusOptions } from "common/content-status";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { ADMIN_PREFIX, urls } from "common/urls";
import { urlByType } from "domain/common/url-utils";
import { getParentFromContentContext } from "domain/common/utils";
import gql from "graphql-tag";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

const GET_KURSSLOEYFE = gql`
  query KurssloeyfeEditData($kurssloeyfeId: ID!) {
    kurssloeyfeById(id: $kurssloeyfeId) {
      id
      status
      title
      context {
        id
        title
      }
    }
  }
`;

const MUTATION = gql`
  mutation UpdateKurssloeyfe($input: UpdateKurssloeyfeDetailsInput!) {
    updateKurssloeyfeDetails(input: $input) {
      kurssloeyfe {
        id
      }
    }
  }
`;

interface FormValues {
  status: ContentStatus;
  title: string;
}

type RouteProps = {
  kurssloeyfeId: string;
};

export const KurssloeyfeEditPage: React.FC = () => {
  const { kurssloeyfeId } = useParams<RouteProps>();
  const navigate = useNavigate();
  const previousPath = usePreviousPath();

  if (!kurssloeyfeId) {
    throw new Error("Unexpected error: MissingkurssloeyfeId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    KurssloeyfeEditData,
    KurssloeyfeEditDataVariables
  >(GET_KURSSLOEYFE, {
    variables: {
      kurssloeyfeId,
    },
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      status: data?.kurssloeyfeById?.status,
    },
  });

  const [mutate, { error, loading }] = useMutation<
    UpdateKurssloeyfe,
    UpdateKurssloeyfeVariables
  >(MUTATION);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.kurssloeyfeById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { title, context } = data.kurssloeyfeById;
  const parent = getParentFromContentContext(context);

  if (!parent) {
    throw new Error("Found kurssloeyfe without parent element.");
  }

  function exitEditPage() {
    if (previousPath && !previousPath.includes(ADMIN_PREFIX)) {
      navigate(-1);
    } else if (parent) {
      navigate(urlByType(parent.__typename, parent.id, true));
    } else {
      navigate(urls.admin);
    }
  }

  const onSubmit = async (content: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          id: kurssloeyfeId,
          status: { updatedValue: content.status },
          title: { updatedValue: content.title },
        },
      },
    });

    if (result.data) {
      exitEditPage();
    }
  };

  return (
    <AdministrationLayout title={title}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TextField
            title="Kurstittel"
            fieldProps={methods.register("title", {
              required: "Du må gi kurset en tittel",
            })}
            defaultValue={title}
          />

          <Select
            title="Status"
            options={contentStatusOptions()}
            fieldProps={methods.register("status")}
          />

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit
            onCancel={exitEditPage}
            submitText="Lagre"
            isLoading={loading}
          />
        </form>
      </FormProvider>
    </AdministrationLayout>
  );
};
